import type { ImperativePanelHandle } from "react-resizable-panels";

import React from "react";

import ChatFileDropZone from "@agent-ui/features/chat/components/ChatFileDropZone";
import MobileChatFooter from "@agent-ui/features/chat/components/MobileChatFooter";
import ReactEditorChat from "@agent-ui/features/chat/components/ReactEditorChat";
import ReactEditorGlobalHotkeysListener from "@agent-ui/features/chat/components/ReactEditorGlobalHotkeysListener";
import { useOpencodeEventRouter } from "@agent-ui/features/chat/hooks/useOpencodeEventRouter";
import LoadingHeader from "@agent-ui/features/page-renderer/components/LoadingHeader";
import ModifiablePageRenderer from "@agent-ui/features/page-renderer/components/ModifiablePageRenderer";
import { PagePreviewCard } from "@agent-ui/features/page-renderer/components/navbar/AddTabModal";
import PageTabBar from "@agent-ui/features/page-renderer/components/navbar/PageTabBar";
import PageRenderControls from "@agent-ui/features/page-renderer/components/PageRenderControls";
import ResizableViewport from "@agent-ui/features/page-renderer/components/ResizableViewport";
import { usePageTabsActions } from "@agent-ui/features/page-renderer/store/pageTabsStore";
import VersionHistory from "@agent-ui/features/version-history/VersionHistory";
import { useSandboxFiles } from "@agent-ui/hooks/useSandboxFiles";
import {
  useEditorStoreActions,
  useMobileChatMode,
  usePanelView,
} from "@agent-ui/store/editorStore";
import {
  useSandboxClearOverride,
  useSandboxNavigate,
  useSandboxPreviewBaseUrl,
} from "@agent-ui/store/sandboxNavigationStore";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { useSidebar } from "@replo/design-system/components/sidebar/Sidebar";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import ManageChats from "@v2/features/chat/components/chat-elements/ManageChats";
import { ChatFileDropZoneProvider } from "@v2/features/chat/hooks/useChatFileDropZone";
import BackendStagingBanner from "@v2/features/debug/BackendStagingBanner";
import DebugPublishStatusOverrideBanner from "@v2/features/debug/DebugPublishStatusOverrideBanner";
import DesignLibraryPanel from "@v2/features/design-library/DesignLibraryPanel";
import { ModifierQueueProvider } from "@v2/features/modifiers/store/ModifierQueueStoreContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { z } from "zod";

/**
 * Listens for changes in the panel view, and if we go to chat, then auto-collapse the sidebar
 * so there's more space for the chat.
 *
 * Note (Noah, 2025-08-30): Kind of weird to have a component for this but it's
 * the only way to respond to chat changes and trigger sidebar changes, since
 * shadcn sidebar only exposes the toggleSidebar through a hook. I didn't do
 * this in the main chat component since that would mean it would rerender
 * unnecessarily when the sidebar changed.
 */
function ChatPanelSidebarListener() {
  const panelView = usePanelView();
  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;
  useOnValueChange(panelView, (newPanelView) => {
    if (newPanelView === "chat" && isSidebarExpanded) {
      toggleSidebar();
    }
  });
  return null;
}

function ReactEditor() {
  // NOTE (Max, 2026-02-11): Route real-time OpenCode session events (via WebSocket)
  // into shared chat/session state before child chat components render.
  useOpencodeEventRouter();

  const { project } = useCurrentProjectContext();
  const [searchParams] = useSearchParams();
  const pageRouteParam = searchParams.get("pageRoute");

  const [isUsingBackendStaging] = useLocalStorageState(
    "replo.debug.useBackendStaging",
    false,
    { schema: z.boolean() },
  );

  // Create a ref for the main container to handle keyboard events
  const reactEditorContainerRef = React.useRef<HTMLDivElement>(null);

  if (!project) {
    return null;
  }

  return (
    <>
      {isUsingBackendStaging && <BackendStagingBanner />}
      <DebugPublishStatusOverrideBanner />
      <ReactEditorContent
        pageRouteParam={pageRouteParam}
        reactEditorContainerRef={reactEditorContainerRef}
      />
      <ChatPanelSidebarListener />
    </>
  );
}

// TODO (Berkley, 2026-03-11, REPL-25023): This is a temporary UI component to allow devs to select a page from the chat.
// Will be removed once we spin up a chat sandbox on mount
function PagePicker({ sessionUuid }: { sessionUuid: string }) {
  const { agent } = useReploAgent();
  const projectId = useCurrentProjectId();
  const sandboxNavigate = useSandboxNavigate();
  const previewBaseUrl = useSandboxPreviewBaseUrl();

  const { files, isLoading } = useSandboxFiles({
    agent,
    projectId: projectId ?? null,
    sessionUuid,
  });

  return (
    <div className="h-full flex flex-col p-3 overflow-y-auto">
      <div className="px-3 pt-3 pb-3">
        <span className="text-2xs font-medium text-muted uppercase tracking-wider">
          Select a page to preview
        </span>
      </div>
      {isLoading && (
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <div className="w-full aspect-[16/10] rounded bg-sheet animate-pulse" />
              <div className="h-3 w-20 rounded bg-sheet animate-pulse" />
            </div>
          ))}
        </div>
      )}
      {!isLoading && files.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-xs text-muted">No pages found</span>
        </div>
      )}
      {!isLoading && files.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {files.map((file) => (
            <PagePreviewCard
              key={file.route}
              file={file}
              previewBaseUrl={previewBaseUrl}
              onClick={() => sandboxNavigate(file.route)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReactEditorContent({
  pageRouteParam,
  reactEditorContainerRef,
}: {
  pageRouteParam: string | null;
  reactEditorContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const panelView = usePanelView();
  const [searchParams] = useSearchParams();
  const sessionUuid = searchParams.get("sessionUuid");
  const { setPanelView, setIsPanelResizing, setMobileChatMode } =
    useEditorStoreActions();

  const isMobile = useIsMobile();

  const mobileChatMode = useMobileChatMode();

  const chatPanelRef = React.useRef<ImperativePanelHandle>(null);

  const { openTab } = usePageTabsActions();
  React.useEffect(() => {
    if (pageRouteParam) {
      openTab({
        id: pageRouteParam,
        content: {
          type: "page",
          pageRoute: pageRouteParam,
          status: null,
        },
      });
    }
  }, [pageRouteParam, openTab]);

  const clearSandboxOverride = useSandboxClearOverride();
  const prevPageRouteParamRef = React.useRef(pageRouteParam);
  if (prevPageRouteParamRef.current !== pageRouteParam) {
    prevPageRouteParamRef.current = pageRouteParam;
    if (pageRouteParam) {
      clearSandboxOverride();
    }
  }

  const canPreview = Boolean(pageRouteParam);
  const showPagePicker = !canPreview && Boolean(sessionUuid);
  const effectiveMobileMode = canPreview ? mobileChatMode : "chat";
  const hasPageData = Boolean(useSandboxPreviewBaseUrl());

  useOnValueChange(sessionUuid, (current, previous) => {
    if (current === "" && previous !== "" && isMobile) {
      setMobileChatMode("chat");
    }
  });

  const getPanelTitle = () => {
    return exhaustiveSwitch({ type: panelView })({
      chat: "Chat",
      history: "Version History",
      designLibrary: "Design Library",
      pageSettings: "Page Settings",
      manageChats: "Manage Chats",
    });
  };

  const panelContent = (
    <>
      {exhaustiveSwitch({ type: panelView })({
        chat: <ReactEditorChat />,
        history: <VersionHistory />,
        designLibrary: <DesignLibraryPanel />,
        // Note (Noah, 2026-02-23): Disabled page settings panel until it can be migrated to use opencode data model
        pageSettings: null,
        manageChats: <ManageChats />,
      })}
    </>
  );

  const desktopLayout = (
    <div className="relative h-full w-full overflow-hidden">
      <PanelGroup
        direction="horizontal"
        autoSaveId="replo-editor-panels"
        className="h-full w-full overflow-hidden"
      >
        <Panel
          ref={chatPanelRef}
          defaultSize={canPreview || showPagePicker ? 33 : 100}
          minSize={20}
          collapsible
          collapsedSize={0}
          className="h-full"
          order={1}
        >
          <div className="h-full flex flex-col gap-5 min-h-0 min-w-0 items-stretch overflow-hidden">
            {panelContent}
          </div>
        </Panel>

        {(canPreview || showPagePicker) && (
          <>
            <PanelResizeHandle
              className="w-2 flex items-center justify-center cursor-col-resize"
              onDragging={setIsPanelResizing}
            >
              <div className="w-0.5 h-8 rounded-full bg-border" />
            </PanelResizeHandle>

            <Panel defaultSize={67} minSize={30} className="h-full" order={2}>
              {canPreview ? (
                <div className="relative h-full flex flex-col w-full min-w-0 shadow-2xs border-0.5 border-border rounded-lg bg-white overflow-hidden">
                  <PageTabBar />
                  <PageRenderControls
                    isLoading={!hasPageData}
                    pageRoute={pageRouteParam ?? null}
                  />
                  <div className="top-0 left-0 w-full relative shrink-0">
                    <LoadingHeader pageRoute={pageRouteParam ?? ""} />
                  </div>
                  <ResizableViewport>
                    {pageRouteParam ? (
                      <div className="flex flex-col h-full w-full">
                        <ModifiablePageRenderer
                          pageRoute={pageRouteParam}
                          className="rounded-t-none w-full rounded-b-lg"
                          isInteractive
                        />
                      </div>
                    ) : null}
                  </ResizableViewport>
                </div>
              ) : (
                <div className="relative h-full flex flex-col w-full min-w-0 shadow-2xs border-0.5 border-border rounded-lg bg-white overflow-hidden">
                  <PagePicker sessionUuid={sessionUuid!} />
                </div>
              )}
            </Panel>
          </>
        )}
      </PanelGroup>
    </div>
  );

  return (
    <ModifierQueueProvider>
      <ChatFileDropZoneProvider>
        <ChatFileDropZone className="h-full w-full">
          <div
            ref={reactEditorContainerRef}
            className="flex flex-col h-full w-full outline-none overflow-hidden"
            tabIndex={-1}
          >
            <ReactEditorGlobalHotkeysListener
              containerRef={reactEditorContainerRef}
            />

            {!isMobile && desktopLayout}

            {isMobile && (
              <>
                {panelView === "chat" && effectiveMobileMode === "chat" && (
                  <div className="flex-1 min-h-0 w-full overflow-hidden">
                    <ReactEditorChat />
                  </div>
                )}

                {canPreview &&
                  !(panelView === "chat" && effectiveMobileMode === "chat") && (
                    <div className="flex-1 min-h-0 flex flex-col w-full">
                      {pageRouteParam && (
                        <ModifiablePageRenderer
                          pageRoute={pageRouteParam}
                          className="w-full flex-1"
                          isInteractive
                        />
                      )}
                    </div>
                  )}

                <Drawer
                  open={panelView !== "chat"}
                  onOpenChange={(open) => {
                    if (!open) {
                      setPanelView("chat");
                    }
                  }}
                  title={getPanelTitle()}
                >
                  <>
                    {panelView === "history" && <VersionHistory />}
                    {panelView === "designLibrary" && <DesignLibraryPanel />}
                    {/* Note (Noah, 2026-02-23): Disabled page settings panel until it can be migrated to use opencode data model */}
                    {panelView === "pageSettings" && null}
                  </>
                </Drawer>

                <MobileChatFooter
                  pageRoute={pageRouteParam ?? null}
                  isLoading={!hasPageData}
                  mode={effectiveMobileMode}
                  onModeChange={(mode) => {
                    if (mode === "preview" && !canPreview) {
                      setMobileChatMode("chat");
                      return;
                    }
                    setMobileChatMode(mode);
                  }}
                />
              </>
            )}
          </div>
        </ChatFileDropZone>
      </ChatFileDropZoneProvider>
    </ModifierQueueProvider>
  );
}

export default ReactEditor;
